import React, { useState, useEffect, useRef } from 'react'
import { Form, Card, Space, Row, Col, Typography, Button } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheckCircle,
  faXmarkCircle,
  faAllergies,
  faPills,
  faSyringe,
  faVirus,
  faUserGroup,
  faPersonRunning,
  faBrain,
  faStethoscope,
  faClipboardList
} from '@fortawesome/free-solid-svg-icons'
import dayjs from 'dayjs'
import DesktopLifestyle from './DesktopLifestyle'
import DesktopAllergies from './DesktopAllergies'
import DesktopMentalHealth from './DesktopMentalHealth'
import DesktopChronicIllnesses from './DesktopChronicIllnesses'
import DesktopFamilyHistory from './DesktopFamilyHistory'
import DesktopCovidInfo from './DesktopCovidInfo'
import DesktopGeneralMeds from './DesktopGeneralMeds'
import DesktopSurgeries from './DesktopSurgeries'
import DesktopSectionHeader from './DesktopSectionHeader'
import '../style/DesktopMedicalHistoryForm.css'
import DesktopMedicalHistoryLoadingOverlay from './LoadingOverlay'
import { useSelector } from 'react-redux'

const { Title, Text } = Typography

const DesktopMedicalHistoryForm = ({ data, fieldValues }) => {
  const [saving, setSaving] = useState(false)
  const [selectedFacet, setSelectedFacet] = useState(null)
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const patMedicalHistory = useSelector((state) => state.patMedHist.data) || {}
  const containerRef = useRef(null)

  useEffect(() => {
    if (containerRef.current && selectedFacet) {
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [selectedFacet])

  const medHistSections = [
    {
      title: 'Allergies',
      key: 'allergies',
      icon: faAllergies,
      gradient: 'linear-gradient(135deg, #2EB67D 0%, #3DD598 100%)',
      primaryColor: '61, 213, 152',
      description: 'Food, medication, and environmental allergies'
    },
    {
      title: 'Chronic Illnesses',
      key: 'chronicIllnesses',
      icon: faStethoscope,
      gradient: 'linear-gradient(135deg, #9267E5 0%, #A783F8 100%)',
      primaryColor: '167, 131, 248',
      description: 'Long-term health conditions'
    },
    {
      title: 'General Medications',
      key: 'meds',
      icon: faPills,
      gradient: 'linear-gradient(135deg, #F57C00 0%, #F9A825 100%)',
      primaryColor: '249, 168, 37',
      description: 'Current and past medications'
    },
    {
      title: 'Covid Information',
      key: 'covidInfo',
      icon: faVirus,
      gradient: 'linear-gradient(135deg, #26A69A 0%, #26C6DA 100%)',
      primaryColor: '38, 198, 218',
      description: 'Vaccination status and health history'
    },
    {
      title: 'Family History',
      key: 'familyHistory',
      icon: faUserGroup,
      gradient: 'linear-gradient(135deg, #F1A63B 0%, #F6C15C 100%)',
      primaryColor: '246, 193, 92',
      description: 'Hereditary conditions and family health'
    },
    {
      title: 'Lifestyle',
      key: 'lifestyle',
      icon: faPersonRunning,
      gradient: 'linear-gradient(135deg, #D81B60 0%, #EC407A 100%)',
      primaryColor: '236, 64, 122',
      description: 'Diet, exercise, and lifestyle habits'
    },
    {
      title: 'Mental Health',
      key: 'mentalHealth',
      icon: faBrain,
      gradient: 'linear-gradient(135deg, #5C6BC0 0%, #7986CB 100%)',
      primaryColor: '121, 134, 203',
      description: 'Mental wellness and psychological history'
    },
    {
      title: 'Surgical History',
      key: 'surgicalHistory',
      icon: faSyringe,
      gradient: 'linear-gradient(135deg, #D32F2F 0%, #EF5350 100%)',
      primaryColor: '239, 83, 80',
      description: 'Previous surgeries and procedures'
    }
  ]

  const handleSave = async () => {
    setLoading(true)
    try {
      // Your save logic here
      setLoading(false)
    } catch (error) {
      console.error('Error saving:', error)
      setLoading(false)
    }
  }

  const renderMainView = () => {
    const completedSections = medHistSections.filter((section) => patMedicalHistory[section.key]?.updatedAt).length
    const progressPercentage = Math.round((completedSections / medHistSections.length) * 100)

    return (
      <div className='desktop-medical-history-overview'>
        <DesktopSectionHeader
          icon={faClipboardList}
          title='Medical History'
          subtitle='Complete your medical history to help healthcare providers give you the best possible care.'
          gradient='linear-gradient(135deg, #2EB67D 0%, #3DD598 100%)'
        />

        <div className='desktop-progress-section' style={{ marginBottom: '32px' }}>
          <Card style={{ borderRadius: '12px', boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)' }}>
            <div className='desktop-progress-info'>
              <Text strong style={{ fontSize: '16px', color: '#262626' }}>
                Progress: {completedSections} of {medHistSections.length} sections completed
              </Text>
              <div
                className='desktop-progress-percentage'
                style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: '#16b981',
                  marginTop: '8px'
                }}
              >
                {progressPercentage}%
              </div>
            </div>
            <div
              className='desktop-progress-bar'
              style={{
                width: '100%',
                height: '8px',
                backgroundColor: '#f0f0f0',
                borderRadius: '4px',
                marginTop: '16px',
                overflow: 'hidden'
              }}
            >
              <div
                className='desktop-progress-fill'
                style={{
                  width: `${progressPercentage}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #16b981 0%, #10d4a7 100%)',
                  borderRadius: '4px',
                  transition: 'width 0.5s ease'
                }}
              />
            </div>
          </Card>
        </div>

        <Row gutter={[24, 24]} className='desktop-sections-grid'>
          {medHistSections.map((section) => {
            const isComplete = patMedicalHistory[section.key]?.updatedAt
            return (
              <Col xs={24} sm={12} lg={8} xl={6} key={section.key}>
                <Card
                  className={`desktop-section-card ${isComplete ? 'completed' : 'incomplete'}`}
                  hoverable
                  onClick={() => {
                    setSelectedFacet(section.key)
                  }}
                  style={{
                    '--section-gradient': section.gradient,
                    '--section-color': section.primaryColor || '61, 213, 152',
                    height: '100%'
                  }}
                >
                  <div className='desktop-card-content'>
                    <div className='desktop-card-header'>
                      <div className='desktop-section-icon'>
                        <FontAwesomeIcon icon={section.icon} />
                      </div>
                      <div className='desktop-section-status'>
                        <FontAwesomeIcon
                          icon={isComplete ? faCheckCircle : faXmarkCircle}
                          style={{
                            color: isComplete ? '#10b981' : '#ef4444'
                          }}
                        />
                      </div>
                    </div>

                    <div className='desktop-card-body'>
                      <Title level={4} style={{ color: `rgba(${section.color})`, margin: 0 }}>
                        {section.title}
                      </Title>
                      <Text className='desktop-section-description'>{section.description}</Text>

                      <div className='desktop-section-info'>
                        <Text className='desktop-last-updated'>
                          Last updated:{' '}
                          <span className={isComplete ? 'updated' : 'never-updated'}>
                            {isComplete
                              ? dayjs(patMedicalHistory[section.key].updatedAt).format('DD MMM YYYY')
                              : 'Never'}
                          </span>
                        </Text>
                      </div>
                    </div>
                  </div>
                </Card>
              </Col>
            )
          })}
        </Row>
      </div>
    )
  }

  const renderFacet = () => {
    switch (selectedFacet) {
      case 'lifestyle':
        return <DesktopLifestyle setSaving={setSaving} clientForm={form} setFacet={setSelectedFacet} />
      case 'allergies':
        return (
          <DesktopAllergies
            setSaving={setSaving}
            clientForm={form}
            setFacet={setSelectedFacet}
            saveFunction={handleSave}
            loading={loading}
          />
        )
      case 'mentalHealth':
        return <DesktopMentalHealth setSaving={setSaving} clientForm={form} setFacet={setSelectedFacet} />
      case 'chronicIllnesses':
        return <DesktopChronicIllnesses setSaving={setSaving} clientForm={form} setFacet={setSelectedFacet} />
      case 'meds':
        return <DesktopGeneralMeds setSaving={setSaving} clientForm={form} setFacet={setSelectedFacet} />
      case 'covidInfo':
        return <DesktopCovidInfo setSaving={setSaving} clientForm={form} setFacet={setSelectedFacet} />
      case 'familyHistory':
        return <DesktopFamilyHistory setSaving={setSaving} clientForm={form} setFacet={setSelectedFacet} />
      case 'surgicalHistory':
        return <DesktopSurgeries setSaving={setSaving} clientForm={form} setFacet={setSelectedFacet} />
      default:
        return renderMainView()
    }
  }

  return (
    <>
      <DesktopMedicalHistoryLoadingOverlay
        text='Saving changes...'
        icon={<FontAwesomeIcon icon={faPills} />}
        loading={loading}
        saveLoading={saving}
      />
      <div ref={containerRef} className='desktop-medical-history-content'>
        {renderFacet()}
      </div>
    </>
  )
}

export default DesktopMedicalHistoryForm
